import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DialogActions, DialogContent, Divider, Stack } from '@mui/material';
import { IAppReduxStore } from '../../../../redux/store';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import Documents from '../_parts/Documents';
import DialogFrame from '../_parts/DialogFrame';
import CheckboxTree, { colorConfig } from '../_parts/CheckboxTree';
import { getDocStructureFields } from '../../../../util/DocUtils';
import { ICategoryFields } from '../../ImportTable/ImportTableCategoryCell';
import LabelAndCaption from '../_parts/LabelAndCaption';
import CtaButton from '../_parts/CtaButton';
import DialogHeader from '../_parts/DialogHeader';
import CommonCategory from './_parts/CommonCategory';
import { ContentLayout } from '../_parts/ContentLayout';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import { Area } from '../../../../types';
import { useCategorizationMapper } from '../../../../hooks/useCategorizationMapper';

export default ({ onClose, area }: { onClose: () => void; area: Area }) => {
    const { replicaId, areaName } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'categorizationDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const [fullCheckedCategories, setFullCheckedCategories] = useState<string[]>([]);
    const [halfCheckedCategories, setHalfCheckedCategories] = useState<string[]>([]);

    const { mode, selectedDocIds, selectedDocuments, structureItems, onConfirm, loading } =
        useCategorizationMapper({ replicaId, onClose, area });
    const { loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const loadingSaveForm = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentDetails.loadingSaveForm,
    );
    const categoryCountMap = useMemo(() => {
        const categoryMap = new Map<string, ICategoryFields & { count: number }>();
        selectedDocuments.forEach(doc => {
            getDocStructureFields(doc, mode).forEach(category => {
                const entry = categoryMap.get(category.Id);

                categoryMap.set(category.Id, {
                    ...category,
                    count: (entry?.count ?? 0) + 1,
                });
            });
        });
        return categoryMap;
    }, [selectedDocuments]);

    useEffect(() => {
        const state = Array.from(categoryCountMap.values());

        const half = state.filter(x => x.count < selectedDocuments.length);
        const full = state.filter(x => x.count === selectedDocuments.length);

        setHalfCheckedCategories(half.map(x => x.Id));
        setFullCheckedCategories(full.map(x => x.Id));
    }, [categoryCountMap]);

    const onCheckCategory = (id: string, isChecked: boolean) => {
        if (!isChecked) {
            setHalfCheckedCategories(state => state.filter(x => x !== id));
        }
        setFullCheckedCategories(state => state.filter(x => x !== id).concat(isChecked ? id : []));
    };

    const onRemoveDocument = (removeId: string) => {
        if (area !== 'import') {
            dispatch(removeProjectDocumentsInfoItem({ universalId: removeId }));
        }

        dispatch(
            updateSelectedDocuments({
                replicaId,
                areaName,
                documentIds: selectedDocIds.filter(x => x !== removeId),
            }),
        );
    };

    const onRemoveCommonCategory = (removeId: string) => {
        setFullCheckedCategories(state => state.filter(x => x !== removeId));
    };

    return (
        <DialogFrame open>
            <DialogHeader
                dialogTitle={t('title')}
                onClose={onClose}
                chipLabel={t(`chipLabel${mode}`)}
                chipColor={colorConfig[mode]}
            />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loading || loadingInfoDocs || loadingSaveForm}
                    main={
                        <Stack spacing='12px'>
                            <Documents
                                documents={selectedDocuments.map(doc => ({
                                    universalId: doc.UniversalID,
                                    title:
                                        doc.title ||
                                        (doc as { Subject?: string }).Subject ||
                                        doc.UniversalID,
                                }))}
                                title={t('selectedDocsLabel', { count: selectedDocuments.length })}
                                removeDocument={onRemoveDocument}
                            />
                            <LabelAndCaption
                                label={t(`reviewLabel${mode}`)}
                                caption={t(`reviewCaption${mode}`)}
                            />
                            <>
                                <Documents
                                    key='commonCategories'
                                    documents={structureItems
                                        .filter(x => fullCheckedCategories.includes(x.id))
                                        .map(x => ({ universalId: x.id, title: x.path }))}
                                    title={t(`commonLabel${mode}`, {
                                        count: fullCheckedCategories.length,
                                    })}
                                >
                                    {structureItems
                                        .filter(x => fullCheckedCategories.includes(x.id))
                                        .map(x => (
                                            <div key={x.id}>
                                                <CommonCategory
                                                    id={x.id}
                                                    path={x.path}
                                                    onRemove={onRemoveCommonCategory}
                                                />
                                            </div>
                                        ))}
                                </Documents>
                                <CheckboxTree
                                    withSearch
                                    items={structureItems.map(x => ({
                                        ...x,
                                        label: x.name,
                                        parent: x.parent?.Subject,
                                    }))}
                                    checkedNodes={fullCheckedCategories}
                                    halfCheckedNodes={halfCheckedCategories}
                                    onCheckNode={onCheckCategory}
                                    withStructureIcons={mode}
                                />
                            </>
                        </Stack>
                    }
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingRight: '24px' }}>
                <CtaButton text={t('cancel')} variant='text' onClick={onClose} />
                <CtaButton
                    disabled={selectedDocuments.length === 0 || loadingSaveForm}
                    text={t('confirm')}
                    variant='contained'
                    onClick={() => onConfirm(fullCheckedCategories, halfCheckedCategories)}
                />
            </DialogActions>
        </DialogFrame>
    );
};
